import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as uiSlice from '../../../data/ui.slice';
import * as lookupSlice from '../../../data/lookup.slice';
import * as clientSlice from '../../../data/client.slice';
import { useTranslation } from 'react-i18next';
import clientUtils from '../../../services/clientUtils';
import { Client, ClientContact } from '../../../data/models/client.model';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import SelectMenu from '../../forms/SelectMenu';
import TextInput from '../../forms/TextInput';
import PhoneInput from '../../forms/PhoneInput';

const PREFIX = 'ContactDialog';

const classes = {
  content: `${PREFIX}-content`,
  actions: `${PREFIX}-actions`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiButtonBase-root': {
    fontSize: '15px !important',
  },

  '& .MuiButton-root': {
    padding: theme.spacing(1, 1.75),
  },

  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
    width: 380,
  },

  [`& .${classes.actions}`]: {
    gap: theme.spacing(1),
  },
}));

interface ContactDialogProps {
  title: string;
  open: boolean;
  client: Client;
  contact?: ClientContact;
  close: () => void;
}

export default function ContactDialog({
  title,
  open,
  client,
  contact,
  close,
}: ContactDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const requiredFieldMessage = t('common.requiredField');

  const saveSuccessMessage = useAppSelector(uiSlice.selectSuccessMessage);
  const timeZones = useAppSelector(lookupSlice.selectTimeZones);
  const departments = useAppSelector(
    lookupSlice.selectClientContactDepartments,
  );
  const roles = useAppSelector(lookupSlice.selectClientContactRoles);
  const savingContact = useAppSelector(clientSlice.selectSavingContact);

  const formHook = useForm({
    mode: 'onTouched',
    defaultValues: clientUtils.getContactDefaults(contact),
  });

  React.useEffect(() => {
    const defaults = clientUtils.getContactDefaults(contact);
    formHook.reset(defaults);
  }, [contact]);

  React.useEffect(() => {
    dispatch(lookupSlice.fetchLookupItemsAsync('timeZone'));
    dispatch(lookupSlice.fetchLookupItemsAsync('clientContactDepartment'));
    dispatch(lookupSlice.fetchLookupItemsAsync('clientContactRole'));
  }, []);

  React.useEffect(() => {
    if (saveSuccessMessage) {
      handleClose();
    }
  }, [saveSuccessMessage]);

  const handleClose = () => {
    formHook.reset();
    close();
  };

  const handleSave = (data: any) => {
    const newContact = clientUtils.getContactFromFormData(data, contact?.state);
    if (contact) {
      dispatch(
        clientSlice.updateClientContactAsync(client.clientBC, newContact),
      );
    } else {
      dispatch(
        clientSlice.createClientContactAsync(client.clientBC, newContact),
      );
    }
  };

  return (
    <RootDialog open={open} onClose={handleClose}>
      <DialogTitle>
        {title}
        {savingContact && (
          <LinearProgress style={{ width: '100%' }} color="primary" />
        )}
      </DialogTitle>
      <DialogContent>
        <div className={classes.content}>
          <TextInput
            name="FirstName"
            label={t('client.firstName')}
            control={formHook.control}
            disabled={false}
            required={true}
            rules={{ required: requiredFieldMessage }}
          />
          <TextInput
            name="LastName"
            label={t('client.lastName')}
            control={formHook.control}
            disabled={false}
            required={true}
            rules={{ required: requiredFieldMessage }}
          />
          <TextInput
            name="Title"
            label={t('client.title')}
            control={formHook.control}
            disabled={false}
          />
          <TextInput
            name="Email"
            label={t('client.email')}
            control={formHook.control}
            disabled={false}
            required={true}
            rules={{ required: requiredFieldMessage }}
          />
          <SelectMenu
            label={t('client.timeZone')}
            name="TimeZone"
            values={timeZones}
            valueAttr="ianaStandard"
            labelAttr="timeZoneName"
            required={true}
            control={formHook.control}
            rules={{ required: requiredFieldMessage }}
          />
          <SelectMenu
            label={t('client.department')}
            name="Department"
            values={departments}
            valueAttr="clientContactDepartmentBC"
            labelAttr="clientContactDepartmentName"
            required={true}
            control={formHook.control}
            rules={{ required: requiredFieldMessage }}
          />
          <SelectMenu
            label={t('client.role')}
            name="Role"
            values={roles}
            valueAttr="clientContactRoleBC"
            labelAttr="clientContactRoleName"
            required={true}
            control={formHook.control}
            rules={{ required: requiredFieldMessage }}
          />
          <PhoneInput
            label={t('client.phone')}
            name="Mobile"
            extensionName="MobileExtension"
            control={formHook.control}
            errors={formHook.formState.errors}
          />
          <PhoneInput
            label={t('client.mobilePhone')}
            name="Landline"
            extensionName="LandlineExtension"
            control={formHook.control}
            errors={formHook.formState.errors}
          />
        </div>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant="contained" color="secondary" onClick={handleClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!formHook.formState.isValid || savingContact}
          onClick={formHook.handleSubmit(handleSave)}>
          {t('common.add')}
        </Button>
      </DialogActions>
    </RootDialog>
  );
}
